import type { Direction, Meal, ResolveScanResponse } from '@sentra/contracts';
import { ImageOff, Pencil, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { DirectionButton } from '@/features/direction-button';
import { usePersonThumbnail } from '@/features/persons/images/person-thumbnail';
import { MealButton } from '@/features/scan/meal-button';
import { useFormatters } from '@/i18n/use-format';

/**
 * What the operator sees after a scan resolves: who this is, where they are recorded as being, and
 * the three things that may be done about it.
 *
 * **The photograph is the primary control, not decoration.** AD-66 states it plainly: the supplier
 * printed every code in the batch before any of it was issued, so what stands between a forged band
 * and a person walking in is an operator looking at a stored photograph and at the face in front of
 * them. A record with no photograph is a scan where that control does not exist, and the panel says
 * so out loud rather than leaving an empty square.
 *
 * **The THUMBNAIL is what is fetched, and that is a decision rather than an oversight.** The full
 * image route writes a `person.image_view` audit row on every call, and every audit write takes the
 * chain's advisory lock until commit — on the busiest path in the product, once per person walking
 * through a door. A 256-pixel square is what an operator compares a face against at a desk, and the
 * full photograph is one click away on the record screen, where the disclosure is the point.
 *
 * **Only the directions the SERVER permitted are offered.** `permittedActions` comes back with the
 * resolution, so this screen never renders a button the submission would refuse; the expected
 * direction is marked, because at a gate the common case should be the obvious one.
 *
 * **The pair is drawn by `features/direction-button.tsx`, which the record screen draws it with
 * too.** An entry is green and an exit is red there; what still marks the EXPECTED one is the
 * solid fill and the width, not the hue, so the emphasis survives for somebody who cannot tell
 * the two hues apart. This file keeps the catalogue key, because the word is its own.
 *
 * **The three meal buttons are a SECOND row and a second flag, not a wider first one.** `movable`
 * and `servable` are independent because the two acts spend different things: the scan's
 * idempotency key is consumed once in `presence_events` by a movement and once in `meal_events` by
 * a meal, on two partial unique indexes over two tables that cannot see each other. Collapsing them
 * into one "this resolution can still act" flag would mean a resident arriving at breakfast time
 * has their entry recorded and then has to be scanned AGAIN to be fed, with the client's own
 * burst-dedupe window swallowing the immediate rescan. Each row disables itself once its own act is
 * done, and neither disables the other.
 *
 * **The meal row is absent when `dining` is `null`**, which is what an installation that does not
 * control meals sends. The screen branches on the absence rather than on a flag it fetched
 * separately, so there is no configuration to consult and nothing to get backwards.
 */

/** The catalogue key for each direction, so the word is not chosen at the call site. */
const DIRECTIONS = {
  entry: { key: 'scan.person.entry' },
  exit: { key: 'scan.person.exit' },
} as const satisfies Record<Direction, { readonly key: string }>;

export function ResolvePanel({
  resolved,
  submitting,
  canModify,
  movable,
  servable,
  onSubmit,
  onServe,
  onView,
  onModify,
}: Readonly<{
  resolved: ResolveScanResponse;
  submitting: boolean;
  /** Whether this operator holds `person:write`; the server decides again whatever this says. */
  canModify: boolean;
  /**
   * Whether a movement can still be recorded from this resolution.
   *
   * False once one has been, and that is AD-30 rather than tidiness: the idempotency key belongs to
   * the SCAN, so a second direction submitted from the same resolution would carry the key the
   * first movement consumed and be answered as a replay — a green confirmation for a movement
   * nobody recorded. A second movement is a second scan, which mints a second key.
   */
  movable: boolean;
  /**
   * Whether a meal can still be served from this resolution. Independent of {@link movable}.
   *
   * The same key, spent in a different table. See the module note for why the two flags are two.
   */
  servable: boolean;
  onSubmit: (direction: Direction) => void;
  onServe: (meal: Meal) => void;
  onView: () => void;
  onModify: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const formatters = useFormatters();
  const { person, dining } = resolved;

  return (
    <section
      data-testid="resolve-panel"
      className="grid gap-4 rounded-lg border bg-card p-4 sm:grid-cols-[auto_1fr]"
    >
      <div
        // Named so a visual baseline can MASK it: a stored photograph and a generated name are
        // different on every run, and a screenshot that carried them would be a baseline nobody
        // could ever review. What the baseline is for is the layout and the state.
        data-testid="resolve-photo"
        className="grid size-40 place-items-center overflow-hidden rounded-lg border bg-muted"
      >
        {person.primaryImageId === null ? (
          <span className="grid justify-items-center gap-1 p-2 text-center text-sm text-muted-foreground">
            <UserRound aria-hidden className="size-8" />
            {t('scan.person.noPhotograph')}
          </span>
        ) : (
          <GatePhotograph
            imageId={person.primaryImageId}
            alt={t('scan.person.photograph', { name: person.displayName })}
          />
        )}
      </div>

      <div className="grid content-start gap-3">
        <div data-testid="resolve-identity">
          <h2 className="text-2xl font-semibold">{person.displayName}</h2>
          <p className="text-sm text-muted-foreground">
            {t('scan.person.shortRef')}: {person.shortRef}
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">{t('scan.person.status')}</dt>
            <dd data-person-status={person.status}>{t(`scan.recordStatus.${person.status}`)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t('scan.person.presence')}</dt>
            <dd data-presence-state={person.presenceState}>
              {t(`scan.state.${person.presenceState}`)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t('scan.person.expected')}</dt>
            <dd data-expected-direction={resolved.expectedDirection}>
              {t(DIRECTIONS[resolved.expectedDirection].key)}
            </dd>
          </div>
        </dl>

        {resolved.warnings.length > 0 && (
          <ul className="grid gap-1">
            {resolved.warnings.map((warning) => (
              <li
                key={warning}
                data-gate-warning={warning}
                className="rounded-md border border-warning bg-warning-surface px-3 py-2 text-sm"
              >
                {t(`scan.warning.${warning}`)}
              </li>
            ))}
          </ul>
        )}

        {/*
          The counter's row, above the two directions rather than below them: at a canteen it is the
          only row anybody presses, and at a door it is the one nobody does. Absent entirely when
          this installation does not control meals.
        */}
        {dining !== null && (
          <div data-testid="meal-row" className="grid gap-1">
            <p className="text-sm text-muted-foreground">{t('scan.meal.heading')}</p>
            <div className="flex flex-wrap gap-2">
              {dining.meals
                .filter((state) => dining.permittedMeals.includes(state.meal))
                .map((state) => (
                  <MealButton
                    key={state.meal}
                    meal={state.meal}
                    state={state}
                    suggested={state.meal === dining.suggested}
                    disabled={submitting || !servable}
                    label={t(`scan.meal.${state.meal}`)}
                    servedLabel={
                      state.at === null
                        ? null
                        : t('scan.meal.servedAt', { time: formatters.time(state.at) })
                    }
                    onClick={() => {
                      onServe(state.meal);
                    }}
                  />
                ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {(movable ? resolved.permittedActions : []).map((direction) => {
            const expected = direction === resolved.expectedDirection;
            return (
              <DirectionButton
                key={direction}
                direction={direction}
                expected={expected}
                disabled={submitting}
                label={
                  submitting && expected
                    ? t('scan.person.submitting')
                    : t(DIRECTIONS[direction].key)
                }
                onClick={() => {
                  onSubmit(direction);
                }}
              />
            );
          })}
          <Button type="button" variant="ghost" onClick={onView}>
            {t('scan.person.view')}
          </Button>
          {canModify && (
            <Button type="button" variant="ghost" onClick={onModify}>
              <Pencil aria-hidden />
              {t('scan.person.modify')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The face an operator identifies somebody by, in the three states it can be in.
 *
 * **Held under the gate's own retention class**, so nothing of it outlives the screen: the gate
 * caches no decrypted person after they walk away (`gate.tsx`), and a face is the most personal
 * thing it draws. **A face that could not be loaded SAYS so.** The photograph is the operator's main
 * check that the band belongs to the person holding it, and an empty square there reads as "this
 * person has no photograph" — a different fact, and one the server already warns about by itself.
 */
function GatePhotograph({ imageId, alt }: Readonly<{ imageId: string; alt: string }>): ReactNode {
  const { t } = useTranslation();
  const { state, src } = usePersonThumbnail(imageId, 'gate');

  if (state === 'ready' && src !== null) {
    return (
      <img src={src} alt={alt} className="size-40 object-cover" data-thumbnail-state={state} />
    );
  }
  const failed = state === 'missing' || state === 'failed';
  return (
    <span
      role={failed ? 'alert' : 'status'}
      data-thumbnail-state={state}
      // The foreground ink rather than a warning hue: the panel behind it is `--muted`, which no
      // tone has been measured against, and the icon and the sentence already carry the state.
      className={`grid justify-items-center gap-1 p-2 text-center text-sm ${
        failed ? 'font-medium text-foreground' : 'text-muted-foreground'
      }`}
    >
      {failed ? <ImageOff aria-hidden className="size-8" /> : null}
      {t(failed ? 'person.images.unavailable' : 'person.images.loading')}
    </span>
  );
}
